import { Component, inject, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-encryption',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="encryption-container">
      <header class="header">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          Sichere <span>Verschlüsselung</span>
        </div>
        <button class="btn-back" (click)="goBack()">Zurück</button>
      </header>

      <main class="content">
        <div class="glass form-card">
          <div class="form-header">
            <h2>AES-256 GCM</h2>
            <p>Zero-Footprint Streaming Architektur</p>
          </div>

          <form #uploadForm>
            <div class="file-upload" [class.has-file]="selectedFile()" (click)="fileInput.click()">
              <input type="file" name="file" #fileInput (change)="onFileSelected($event)" accept=".zip,.enc" style="display: none;">
              <div class="upload-icon">
                <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" stroke-width="2" fill="none" *ngIf="!selectedFile()"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" stroke-width="2" fill="none" *ngIf="selectedFile()"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
              </div>
              <div class="upload-text">
                <span *ngIf="!selectedFile()">Datei auswählen (.zip / .enc)</span>
                <span *ngIf="selectedFile()" class="filename">{{ selectedFile()?.name }}</span>
              </div>
            </div>
          </form>

          <div class="form-group" *ngIf="selectedFile()">
            <label for="password-input">Sicherheitsschlüssel</label>
            <div class="password-field-container" [class.focused]="isFocused()">
              <div class="input-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
              </div>
              <input 
                id="password-input"
                [type]="showPassword() ? 'text' : 'password'" 
                [ngModel]="password()" 
                (ngModelChange)="password.set($event)" 
                (focus)="isFocused.set(true)"
                (blur)="isFocused.set(false)"
                placeholder="Passwort eingeben..."
              >
              <button type="button" class="btn-toggle" (click)="togglePassword()">
                <svg *ngIf="!showPassword()" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                <svg *ngIf="showPassword()" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
              </button>
            </div>
          </div>

          <!-- Progress Section -->
          <div class="progress-section" *ngIf="isLoading()">
            <div class="progress-info">
              <span>{{ isDecryptMode() ? 'Entschlüsselung...' : 'Verschlüsselung...' }}</span>
              <span>{{ progress() }}%</span>
            </div>
            <div class="progress-bar-bg">
              <div class="progress-bar-fill" [style.width.%]="progress()"></div>
            </div>
          </div>

          <div class="alert error" *ngIf="error()">
            <span>{{ error() }}</span>
          </div>
          <div class="alert success" *ngIf="success() && !isLoading()">
            <span>{{ success() }}</span>
          </div>

          <button class="btn-action" *ngIf="selectedFile() && !isLoading()" [disabled]="!password() || isLoading()" (click)="processFile()">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none" style="margin-right: 8px;"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            {{ isDecryptMode() ? 'Entschlüsseln & Download' : 'Verschlüsseln & Download' }}
          </button>
        </div>
      </main>

      <div class="glow-orb orb-1"></div>
      <div class="glow-orb orb-2"></div>
      
      <iframe name="downloadFrame" style="display:none;"></iframe>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      min-height: 100vh;
      background: #0b0f1a;
      color: #f8fafc;
      overflow-x: hidden;
      background-image: 
        radial-gradient(at 0% 0%, rgba(99, 102, 241, 0.12) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(168, 85, 247, 0.12) 0px, transparent 50%);
    }

    .encryption-container {
      max-width: 600px;
      margin: 0 auto;
      padding: 2.5rem 1.5rem;
      position: relative;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 3.5rem;
      z-index: 20;
      position: relative;
    }

    .logo {
      font-size: 1.25rem; font-weight: 800; display: flex; align-items: center; gap: 0.6rem;
    }
    .logo svg { color: #818cf8; }
    .logo span { background: linear-gradient(to right, #818cf8, #c084fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

    .btn-back { 
      background: rgba(255, 255, 255, 0.03); 
      border: 1px solid rgba(255,255,255,0.08); 
      color: #94a3b8; 
      padding: 0.5rem 1rem; 
      border-radius: 0.6rem; 
      cursor: pointer; 
      transition: all 0.2s; 
      font-weight: 600;
      font-size: 0.85rem;
    }
    .btn-back:hover { color: #f8fafc; background: rgba(255,255,255,0.06); }

    .content { position: relative; z-index: 10; }

    .glass {
      background: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(24px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 1.75rem;
      padding: 2.5rem;
      width: 100%;
      box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.6);
    }

    .form-header { text-align: center; margin-bottom: 2.5rem; }
    .form-header h2 { margin: 0 0 0.5rem; font-size: 1.75rem; font-weight: 900; background: linear-gradient(to right, #818cf8, #c084fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; letter-spacing: -0.04em; }
    .form-header p { color: #94a3b8; margin: 0; font-size: 0.95rem; opacity: 0.7; }

    .file-upload {
      border: 1.5px dashed rgba(129, 140, 248, 0.2);
      border-radius: 1.25rem;
      padding: 2.5rem 1.5rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      background: rgba(15, 23, 42, 0.4);
      margin-bottom: 2rem;
    }
    .file-upload:hover { border-color: #818cf8; background: rgba(129, 140, 248, 0.06); }
    .file-upload.has-file { border-color: #10b981; background: rgba(16, 185, 129, 0.06); border-style: solid; }

    .upload-icon { color: #818cf8; margin-bottom: 1rem; display: flex; justify-content: center; }
    .upload-text { color: #cbd5e1; font-weight: 500; font-size: 0.9rem; }
    .upload-text .filename { color: #10b981; font-weight: 700; font-size: 1.05rem; }

    .form-group { margin-bottom: 2rem; }
    .form-group label { display: block; margin-bottom: 0.6rem; color: #94a3b8; font-weight: 600; font-size: 0.9rem; }
    
    .password-field-container {
      position: relative;
      display: flex;
      align-items: center;
      background: rgba(15, 23, 42, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.85rem;
      transition: all 0.3s;
    }
    .password-field-container.focused { border-color: #818cf8; background: rgba(15, 23, 42, 1); }

    .input-icon { padding-left: 0.85rem; color: #64748b; display: flex; align-items: center; }
    .password-field-container input { flex: 1; padding: 1rem 0.75rem; background: transparent; border: none; color: white; font-size: 1rem; outline: none; }
    .btn-toggle { background: transparent; border: none; color: #64748b; cursor: pointer; padding: 0.85rem; display: flex; align-items: center; }

    .progress-section { margin-bottom: 2rem; animation: fadeIn 0.3s ease; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .progress-info { display: flex; justify-content: space-between; margin-bottom: 0.6rem; font-size: 0.85rem; font-weight: 600; color: #94a3b8; }
    .progress-bar-bg { height: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 4px; overflow: hidden; }
    .progress-bar-fill { height: 100%; background: linear-gradient(to right, #6366f1, #c084fc); transition: width 0.3s ease; }

    .btn-action {
      width: 100%; padding: 1.1rem; border: none; border-radius: 1rem;
      background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white;
      font-size: 1.1rem; font-weight: 800; cursor: pointer; transition: all 0.3s;
      box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
      display: flex; justify-content: center; align-items: center;
    }
    .btn-action:hover { transform: translateY(-2px); box-shadow: 0 15px 25px -8px rgba(99, 102, 241, 0.5); }
    .btn-action:disabled { opacity: 0.5; cursor: not-allowed; }

    .alert { padding: 1rem; border-radius: 0.85rem; margin-bottom: 1.5rem; font-weight: 600; font-size: 0.9rem; }
    .alert.error { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.15); color: #fca5a5; }
    .alert.success { background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.15); color: #6ee7b7; }

    .glow-orb { position: absolute; border-radius: 50%; filter: blur(120px); opacity: 0.2; pointer-events: none; }
    .orb-1 { top: -5%; left: -5%; width: 400px; height: 400px; background: #6366f1; }
    .orb-2 { bottom: -5%; right: -5%; width: 450px; height: 450px; background: #a855f7; }
  `]
})
export class EncryptionComponent implements OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);

  selectedFile = signal<File | null>(null);
  password = signal<string>('');
  showPassword = signal<boolean>(false);
  isFocused = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  progress = signal<number>(0);
  error = signal<string | null>(null);
  success = signal<string | null>(null);

  private xhr: XMLHttpRequest | null = null;

  isDecryptMode(): boolean {
    const file = this.selectedFile();
    if (!file) return false;
    return file.name.toLowerCase().endsWith('.enc');
  }

  goBack() {
    this.router.navigate(['/launcher']);
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip') && !file.name.toLowerCase().endsWith('.enc')) {
        this.error.set('Bitte wählen Sie nur .zip oder .enc Dateien aus.');
        this.selectedFile.set(null);
        return;
      }
      this.selectedFile.set(file);
      this.error.set(null);
      this.success.set(null);
    }
  }

  togglePassword() {
    this.showPassword.update(s => !s);
  }

  async processFile() {
    const file = this.selectedFile();
    const pwd = this.password();
    if (!file || !pwd) return;

    this.isLoading.set(true);
    this.progress.set(0);
    this.error.set(null);
    this.success.set('Verarbeitung gestartet...');

    const token = this.authService.getGuid() || '';
    const isDecrypt = this.isDecryptMode();
    const baseDownload = isDecrypt ? '/api/encryption/download-decrypt' : '/api/encryption/download-encrypt';

    try {
      // 1. Start Session
      const startUrl = isDecrypt 
        ? `/api/encryption/start-decrypt?x-api-token=${encodeURIComponent(token)}`
        : `/api/encryption/start-encrypt?x-api-token=${encodeURIComponent(token)}&filename=${encodeURIComponent(file.name)}`;

      const startResponse = await fetch(startUrl, {
        method: 'POST',
        headers: {
          'x-encryption-password': pwd
        }
      });
      if (!startResponse.ok) {
        throw new Error(`Session konnte nicht gestartet werden: ${await startResponse.text()}`);
      }
      const { sessionId, downloadToken } = await startResponse.json();

      // 2. Upload Chunks
      const chunkSize = 20 * 1024 * 1024; // 20 MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        // If loading status got cleared (e.g. aborted), stop the loop
        if (!this.isLoading()) {
          return;
        }

        const startByte = chunkIndex * chunkSize;
        const endByte = Math.min((chunkIndex + 1) * chunkSize, file.size);
        const chunkBlob = file.slice(startByte, endByte);

        const formData = new FormData();
        formData.append('file', chunkBlob, `chunk_${chunkIndex}`);

        await new Promise<void>((resolve, reject) => {
          this.xhr = new XMLHttpRequest();
          this.xhr.open('POST', `/api/encryption/upload-chunk?x-api-token=${encodeURIComponent(token)}&sessionId=${sessionId}`);

          this.xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const chunkPercent = event.loaded / event.total;
              const overallPercent = Math.min(
                Math.round(((chunkIndex + chunkPercent) / totalChunks) * 99),
                99
              );
              this.progress.set(overallPercent);
            }
          };

          this.xhr.onload = () => {
            if (this.xhr?.status === 200) {
              resolve();
            } else {
              reject(new Error(`Chunk ${chunkIndex + 1} fehlgeschlagen: ${this.xhr?.responseText || 'Status ' + this.xhr?.status}`));
            }
          };

          this.xhr.onerror = () => {
            reject(new Error('Netzwerkfehler beim Chunk-Upload.'));
          };

          this.xhr.send(formData);
        });
      }

      // 3. Finalize
      const finalizeResponse = await fetch(`/api/encryption/finalize?x-api-token=${encodeURIComponent(token)}&sessionId=${sessionId}`, { method: 'POST' });
      if (!finalizeResponse.ok) {
        throw new Error(`Verarbeitung konnte nicht abgeschlossen werden: ${await finalizeResponse.text()}`);
      }
      const { downloadToken: finalDownloadToken } = await finalizeResponse.json();

      this.progress.set(100);
      this.isLoading.set(false);
      this.success.set('Verarbeitung erfolgreich abgeschlossen!');

      // Trigger download
      const iframe = document.getElementsByName('downloadFrame')[0] as HTMLIFrameElement;
      iframe.src = `${baseDownload}?x-api-token=${encodeURIComponent(token)}&downloadToken=${finalDownloadToken}&filename=${encodeURIComponent(file.name)}`;

    } catch (err: any) {
      this.isLoading.set(false);
      this.error.set(err.message || 'Ein unbekannter Fehler ist aufgetreten.');
      this.success.set(null);
    } finally {
      this.xhr = null;
    }
  }

  ngOnDestroy() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }
}
